import React, { Component } from "react";
import {
  loading,
  alert,
  NavbarAdmin,
  Select,
  GET,
  GET_FILE,
  saveAs,
  DateRangeFields,
  currentLang,
  t,
} from "../../components/CustomComponent.js";
import {
  fill,
  fmtInt,
  fmtBaht,
  fmtBahtOrDash,
  fmtDate,
  WarrantyBadge,
  EmptyBox,
  Skeleton,
  ErrorCard,
  WARRANTY_COLORS,
} from "../../components/AssetHistoryParts.js";
import { pruneParams } from "../../utils/timeTracking.js";
import "../../assets/css/asset-history.css";

// ข้อความ 403 จาก backend เมื่อขอแผนกที่ไม่มีสิทธิ์: "คุณไม่มีสิทธิ์ดูสินทรัพย์ของแผนกนี้"
const NO_DEPARTMENT_PATTERN = "ไม่มีสิทธิ์ดู";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ACCESS_DENIED = "Access denied";
const MAX_RANGE_MONTHS = 24;

const keyToDate = (key) => {
  const m = typeof key === "string" && key.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

// ตรวจช่วงวันที่ก่อนยิง API (E6): "" = ใช้ได้ (ว่างทั้งคู่ = ให้ backend ใช้ 12 เดือนล่าสุด)
const validateRange = (from, to) => {
  if (!from && !to) return "";
  if (!from || !to) return "required";
  if (from > to) return "order";
  const limit = keyToDate(from);
  limit.setMonth(limit.getMonth() + MAX_RANGE_MONTHS);
  return keyToDate(to) > limit ? "too_long" : "";
};

// หน้า ① ภาพรวมสินทรัพย์ - GET admin/asset-history-report/overview (สิทธิ์ asset.read)
// KPI / แท่งแผนก / แถวตาราง คลิกแล้วไปหน้า ② /ad/asset-history-list พร้อมตัวกรอง · แถว Top 10 คลิกไปหน้า ③ /ad/asset-history-detail?uid=
export default class AdminAssetReport extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(window.location.search);
    const departmentUid = (query.get("department_uid") || "").trim();
    this.state = {
      department_uid: UUID_RE.test(departmentUid) ? departmentUid : "",
      start: "",
      end: "",
      data: null,
      fetching: true,
      error: "",
      forbidden: "",
      top_by: "freq", // freq = ซ่อมบ่อย / cost = ค่าใช้จ่ายสูง
      exporting: false,
    };
    this.req_id = 0;
  }

  componentDidMount() {
    loading(false);
    this.Get();
  }

  Get = async () => {
    const { department_uid, start, end } = this.state;
    if (validateRange(start, end)) return;
    const req = ++this.req_id;
    this.setState({ fetching: true, error: "", forbidden: "" });
    const result = await GET(
      "admin/asset-history-report/overview",
      pruneParams({ department_uid, date_from: start, date_to: end }),
      true,
      { skipAccessDeniedAlert: true }
    );
    if (req !== this.req_id) return; // มีคำขอใหม่กว่าแล้ว
    if (result?.status && result.data) {
      const data = result.data;
      this.setState((s) => ({
        data,
        fetching: false,
        // ยังไม่เลือกช่วงวันที่ = แสดงช่วงเริ่มต้นที่ backend ใช้ (12 เดือนล่าสุด) ให้ผู้ใช้เห็นและแก้ต่อได้
        start: s.start || data.range?.from || "",
        end: s.end || data.range?.to || "",
      }));
      return;
    }
    const message = typeof result?.message === "string" ? result.message : "";
    const forbidden = message === ACCESS_DENIED || message.includes(NO_DEPARTMENT_PATTERN) ? message : "";
    this.setState({ data: null, fetching: false, forbidden, error: forbidden ? "" : message || t("ah_error_hint") });
  };

  SetDepartment = (value) => this.setState({ department_uid: value || "" }, this.Get);

  SetRange = ({ start, end }) =>
    this.setState({ start, end }, () => {
      // รอให้เลือกครบทั้งสองช่องก่อนค่อยยิง (ล้างทั้งคู่ = ใช้ค่าเริ่มต้น)
      if (end || !start) this.Get();
    });

  // ไปหน้า ② พร้อมตัวกรอง (แผนกที่เลือกอยู่ติดไปด้วย)
  GoList = (filters = {}) => {
    const params = new URLSearchParams(pruneParams({ department_uid: this.state.department_uid, ...filters }));
    const qs = params.toString();
    window.location.href = `/ad/asset-history-list${qs ? `?${qs}` : ""}`;
  };

  GoDetail = (uid) => {
    window.location.href = `/ad/asset-history-detail?uid=${encodeURIComponent(uid)}`;
  };

  Export = async () => {
    this.setState({ exporting: true });
    loading(true);
    const result = await GET_FILE("admin/asset-history-report/export", pruneParams({ department_uid: this.state.department_uid }));
    loading(false);
    this.setState({ exporting: false });
    if (result?.status && result.blob) {
      saveAs(result.blob, result.fileName || "Asset_History.xlsx");
    } else {
      alert("", "warning", t("warning"), result?.message || t("ah_export_failed"));
    }
  };

  // ---------------------------------------------------------------- render
  renderKpis(kpi) {
    const cards = [
      { key: "total", cls: "total", label: t("ah_kpi_total"), value: kpi.total, sub: t("ah_kpi_total_sub"), filters: {} },
      { key: "active", cls: "active", label: t("ah_s_active"), value: kpi.active, sub: fill(t("ah_pct_of_total"), { n: kpi.active_pct ?? 0 }), filters: { status: "active" } },
      { key: "inactive", cls: "inactive", label: t("ah_s_inactive"), value: kpi.inactive, sub: t("ah_kpi_inactive_sub"), filters: { status: "inactive" } },
      { key: "in", cls: "inw", label: t("ah_kpi_in_warranty"), value: kpi.in_warranty, sub: fill(t("ah_pct_of_total"), { n: kpi.in_warranty_pct ?? 0 }), filters: { warranty: "in" } },
      { key: "near", cls: "near", label: t("ah_kpi_near"), value: kpi.near_expiry, sub: t("ah_kpi_near_sub"), filters: { warranty: "near" } },
      { key: "expired", cls: "expired", label: t("ah_kpi_expired"), value: kpi.expired, sub: fill(t("ah_kpi_expired_sub"), { n: fmtInt(kpi.no_warranty_data) }), filters: { warranty: "expired" } },
    ];
    return (
      <div className="row g-3 mb-4" data-testid="ah-kpis">
        {cards.map((c) => (
          <div className="col-6 col-md-4 col-xl-2" key={c.key}>
            <button type="button" className={`ah-kpi ${c.cls} rounded-4 p-3 h-100`} onClick={() => this.GoList(c.filters)} data-testid={`ah-kpi-${c.key}`}>
              <div className="l">{c.label}</div>
              <div className="v">{fmtInt(c.value)}</div>
              <div className="s">{c.sub}</div>
            </button>
          </div>
        ))}
      </div>
    );
  }

  renderDonut(id, segments, total, centerValue, centerLabel) {
    let acc = 0;
    const gradient = total
      ? `conic-gradient(${segments.map((s) => { const from = acc; acc += (s.count / total) * 100; return `${s.color} ${from}% ${acc}%`; }).join(",")})`
      : "#E8ECF2";
    return (
      <div>
        <div className="ah-donut" style={{ background: gradient }} data-testid={id}>
          <div className="ct">{centerValue}<small>{centerLabel}</small></div>
        </div>
        <div className="ah-legend">
          {segments.map((s) => (
            <span key={s.key}><i style={{ background: s.color }} />{s.label}<b>{fmtInt(s.count)}</b></span>
          ))}
        </div>
      </div>
    );
  }

  renderCharts(data) {
    const { kpi, status_donut = [], warranty_donut = [] } = data;
    const department_rows = (data.department_rows || []).filter((b) => (b.total || 0) > 0);
    const countOf = (list, key) => list.find((x) => x.key === key)?.count || 0;
    const total = kpi.total || 0;
    const statusSegs = [
      { key: "active", count: countOf(status_donut, "active"), color: "#21B25A", label: t("ah_s_active") },
      { key: "inactive", count: countOf(status_donut, "inactive"), color: "#AEB8C6", label: t("ah_s_inactive") },
    ];
    const warrantySegs = [
      { key: "in", count: countOf(warranty_donut, "in"), color: WARRANTY_COLORS.in, label: t("ah_legend_in") },
      { key: "near", count: countOf(warranty_donut, "near"), color: WARRANTY_COLORS.near, label: t("ah_legend_near") },
      { key: "expired", count: countOf(warranty_donut, "expired"), color: WARRANTY_COLORS.expired, label: t("ah_legend_expired") },
      { key: "none", count: countOf(warranty_donut, "none"), color: WARRANTY_COLORS.none, label: t("ah_legend_none") },
    ];
    return (
      <div className="row g-3 mb-4">
        <div className="col-lg-5">
          <div className="bg-white rounded-4 shadow-sm p-4 h-100">
            <h6 className="fw-bold text-dark mb-3">{t("ah_chart_status")}</h6>
            <div className="ah-donuts">
              {this.renderDonut("ah-donut-status", statusSegs, total, fmtInt(total), t("ah_assets"))}
              {this.renderDonut("ah-donut-warranty", warrantySegs, total, `${kpi.in_warranty_pct ?? 0}%`, t("ah_in_warranty_short"))}
            </div>
          </div>
        </div>
        <div className="col-lg-7">
          <div className="bg-white rounded-4 shadow-sm p-4 h-100">
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center">
              {t("ah_chart_bu")}
              <span className="text-12 text-secondary fw-normal ms-auto">{t("ah_click_row_hint")}</span>
            </h6>
            {!department_rows.length ? (
              <EmptyBox title={t("ah_empty_bu")} />
            ) : (
              <div data-testid="ah-sbars">
                {department_rows.map((b) => {
                  const tt = b.total || 0;
                  const w = (n) => (tt ? `${((n || 0) / tt) * 100}%` : "0%");
                  const drill = !!b.department_uid; // แถว "ไม่ระบุแผนก" กรองไม่ได้
                  const tip = `${t("ah_legend_in")} ${fmtInt(b.warranty_in)} · ${t("ah_legend_near")} ${fmtInt(b.warranty_near)} · ${t("ah_legend_expired")} ${fmtInt(b.warranty_expired)} · ${t("ah_legend_none")} ${fmtInt(b.warranty_none)}`;
                  return (
                    <div key={b.department_uid || "none"} className={`ah-sbar ${drill ? "" : "static"}`} title={tip} onClick={drill ? () => this.GoList({ department_uid: b.department_uid }) : undefined}>
                      <div className="nm"><span className="c">{b.department_code || "-"}</span>{b.department_uid ? b.department_name : t("ah_department_none")}</div>
                      <div className="track">
                        <div className="ah-c-in" style={{ width: w(b.warranty_in) }} />
                        <div className="ah-c-near" style={{ width: w(b.warranty_near) }} />
                        <div className="ah-c-exp" style={{ width: w(b.warranty_expired) }} />
                        <div className="ah-c-no" style={{ width: w(b.warranty_none) }} />
                      </div>
                      <div className="tt">{fmtInt(tt)}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="ah-slegend">
              <span><i className="ah-c-in" />{t("ah_legend_in")}</span>
              <span><i className="ah-c-near" />{t("ah_legend_near")}</span>
              <span><i className="ah-c-exp" />{t("ah_legend_expired")}</span>
              <span><i className="ah-c-no" />{t("ah_legend_none")}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCost(row) {
    return (
      <>
        {fmtBaht(row.parts_cost)}
      </>
    );
  }

  renderDepartmentTable(data) {
    const { totals } = data;
    const department_rows = (data.department_rows || []).filter((b) => (b.total || 0) > 0);
    const th = (label, cls = "") => <th className={`border-0 ${cls}`}>{label}</th>;
    return (
      <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
        <div className="px-4 pt-3 pb-2">
          <h6 className="fw-bold text-dark mb-0">{t("ah_table_department")}</h6>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 ah-table" data-testid="ah-bu-table">
            <thead>
              <tr className="text-dark fw-bold border-bottom border-light">
                {th(t("ah_col_bu"))}
                {th(t("ah_col_total"), "num")}
                {th(t("ah_s_active"), "num")}
                {th(t("ah_s_inactive"), "num")}
                {th(t("ah_col_in_warranty"), "num")}
                {th(t("ah_col_near"), "num")}
                {th(t("ah_col_expired"), "num")}
                {th(t("ah_col_no_data"), "num")}
                {th(t("ah_col_pct"), "num")}
                {th(t("ah_col_wo"), "num")}
                {th(t("ah_col_cost"), "num")}
              </tr>
            </thead>
            <tbody>
              {!department_rows.length ? (
                <tr><td colSpan={11}><EmptyBox title={t("ah_empty_bu")} /></td></tr>
              ) : (
                department_rows.map((b) => (
                  <tr key={b.department_uid || "none"} className={`border-bottom border-light ${b.department_uid ? "ah-row" : ""}`} onClick={b.department_uid ? () => this.GoList({ department_uid: b.department_uid }) : undefined} data-testid="ah-bu-row">
                    <td className="wrap">{b.department_uid ? <><span className="ah-bd bu">{b.department_code || "-"}</span> {b.department_name}</> : <span className="text-secondary">{t("ah_department_none")}</span>}</td>
                    <td className="num fw-bold">{fmtInt(b.total)}</td>
                    <td className="num">{fmtInt(b.active)}</td>
                    <td className="num">{fmtInt(b.inactive)}</td>
                    <td className="num">{fmtInt(b.in_warranty)}</td>
                    <td className="num" style={{ color: "#A86F00" }}>{fmtInt(b.warranty_near)}</td>
                    <td className="num" style={{ color: "#C93636" }}>{fmtInt(b.warranty_expired)}</td>
                    <td className="num">{fmtInt(b.warranty_none)}</td>
                    <td className="num">{b.in_warranty_pct ?? 0}%</td>
                    <td className="num">{fmtInt(b.work_orders)}</td>
                    <td className="num">{this.renderCost(b)}</td>
                  </tr>
                ))
              )}
              {totals && department_rows.length ? (
                <tr className="tot">
                  <td>{t("ah_total_row")}</td>
                  <td className="num">{fmtInt(totals.total)}</td>
                  <td className="num">{fmtInt(totals.active)}</td>
                  <td className="num">{fmtInt(totals.inactive)}</td>
                  <td className="num">{fmtInt(totals.in_warranty)}</td>
                  <td className="num">{fmtInt(totals.warranty_near)}</td>
                  <td className="num">{fmtInt(totals.warranty_expired)}</td>
                  <td className="num">{fmtInt(totals.warranty_none)}</td>
                  <td className="num">{totals.in_warranty_pct ?? 0}%</td>
                  <td className="num">{fmtInt(totals.work_orders)}</td>
                  <td className="num">{this.renderCost(totals)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderStrip(ws) {
    const pending = Number(ws.parts_cost_pending_sap) || 0;
    // ยอดรวมไม่นับรายการที่ยังรอ SAP (E4) - แสดงยอดที่รอแยกไว้ใต้ตัวเลข
    const cost = Math.max(0, (Number(ws.parts_cost) || 0) - pending);
    const byType = ws.by_type || {};
    const tiles = [
      { bg: "#1E8DB8", label: t("ah_strip_wo"), value: fmtInt(ws.work_orders), sub: `${t("ah_type_cm")} ${fmtInt(byType.cm)} · ${t("ah_type_pm")} ${fmtInt(byType.pm)} · ${t("ah_type_wr")} ${fmtInt(byType.wr)} · ${t("ah_type_ins")} ${fmtInt(byType.ins)}` },
      { bg: "#3B82F6", label: t("ah_strip_response"), value: ws.avg_response_label ? `${ws.avg_response_label} ${t("ah_hrs_min")}` : "-", sub: t("ah_strip_response_sub") },
      { bg: "#45C973", label: t("ah_strip_duration"), value: ws.avg_duration_label ? `${ws.avg_duration_label} ${t("ah_hrs_min")}` : "-", sub: t("ah_strip_duration_sub") },
      { bg: "#A855F7", label: t("ah_strip_cost"), value: fmtBaht(cost), sub: t("ah_strip_cost_sub"), testid: "ah-strip-cost" },
    ];
    return (
      <div className="row g-3 mb-4" data-testid="ah-strip">
        {tiles.map((x) => (
          <div className="col-12 col-md-6 col-xl-3" key={x.label}>
            <div className="ah-strip-tile" style={{ background: x.bg }} data-testid={x.testid}>
              <div className="l">{x.label}</div>
              <div className="v">{x.value}</div>
              <div className="s">{x.sub}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderTop(data) {
    const { top_by } = this.state;
    const rows = (top_by === "freq" ? data.top_frequent : data.top_cost) || [];
    const th = (label, cls = "") => <th className={`border-0 ${cls}`}>{label}</th>;
    return (
      <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
        <div className="ah-warn-head d-flex flex-wrap align-items-center gap-2 px-4 py-3">
          <span>⟲ {t("ah_top_title")}</span>
          <span className="ms-auto d-flex gap-2">
            {[["freq", "ah_top_freq"], ["cost", "ah_top_cost"]].map(([key, label]) => (
              <button key={key} type="button" className={`btn btn-sm rounded-3 ${top_by === key ? "btn-primary" : "btn-white border"}`} onClick={() => this.setState({ top_by: key })} data-testid={`ah-top-${key}`}>
                {t(label)}
              </button>
            ))}
          </span>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 ah-table" data-testid="ah-top-table">
            <thead>
              <tr className="text-dark fw-bold border-bottom border-light">
                {th("#")}
                {th(t("ah_col_asset"))}
                {th(t("ah_col_bu"))}
                {th(t("ah_col_customer"))}
                {th(t("ah_col_repairs"), "num")}
                {th(t("ah_col_labor"), "num")}
                {th(t("ah_col_parts"), "num")}
                {th(t("ah_col_total_cost"), "num")}
                {th(t("ah_col_warranty"))}
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr><td colSpan={9}><EmptyBox title={t("ah_empty_top")} /></td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.uid} className="border-bottom border-light ah-row" onClick={() => this.GoDetail(r.uid)} data-testid="ah-top-row">
                    <td>{r.rank ?? i + 1}</td>
                    <td className="ah-prod wrap"><b>{r.product_name || "-"}</b><span className="ref">{r.asset_code}</span></td>
                    <td className="nw" title={r.department?.name || ""}>{r.department ? <span className="ah-bd bu">{r.department.code || r.department.name}</span> : <span className="text-secondary">-</span>}</td>
                    <td className="wrap">{r.customer_name || "-"}<div className="sub">{[r.location_name, r.location_province].filter(Boolean).join(" · ")}</div></td>
                    <td className="num fw-bold">{fmtInt(r.repair_count)}</td>
                    <td className="num text-secondary">{fmtBahtOrDash(r.labor_cost)}</td>
                    <td className="num">{fmtBaht(r.parts_cost)}</td>
                    <td className="num fw-bold">{fmtBaht(r.total_cost)}</td>
                    <td className="nw"><WarrantyBadge warranty={r.warranty} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderBody() {
    const { data, fetching, error, forbidden } = this.state;
    if (forbidden) {
      return (
        <div className="bg-white rounded-4 shadow-sm p-5 mb-4 text-center" data-testid="ah-forbidden">
          <h4 className="fw-bold text-dark">{forbidden === ACCESS_DENIED ? t("ah_forbidden") : forbidden}</h4>
          <p className="text-secondary text-14 mb-3">{t("ah_forbidden_hint")}</p>
          <button type="button" className="btn btn-primary px-4 py-2 fw-bold rounded-3" onClick={() => this.SetDepartment("")}>{t("ah_all_bu")}</button>
        </div>
      );
    }
    if (error) return <ErrorCard message={error} onRetry={this.Get} />;
    if (!data) return <Skeleton />;
    const ws = data.work_summary || {};
    return (
      <div style={fetching ? { opacity: 0.55, pointerEvents: "none" } : undefined} data-testid="ah-body">
        {this.renderKpis(data.kpi)}
        {this.renderCharts(data)}
        {this.renderDepartmentTable(data)}
        {this.renderStrip(ws)}
        {this.renderTop(data)}
      </div>
    );
  }

  render() {
    const { department_uid, start, end, data, exporting } = this.state;
    const rangeError = validateRange(start, end);
    const departmentOptions = [{ value: "", label: t("ah_all_bu") }, ...(data?.department_options || []).map((d) => ({ value: d.uid, label: [d.code, d.name].filter(Boolean).join(" ") }))];
    // แผนกที่เลือกจากลิงก์แต่ไม่อยู่ในรายการ active (เช่น แผนกปิดใช้งาน) - backend คืนชื่อใน data.department
    if (department_uid && !departmentOptions.some((o) => o.value === department_uid)) {
      const d = data?.department;
      departmentOptions.push({ value: department_uid, label: d ? [d.code, d.name].filter(Boolean).join(" ") : department_uid });
    }
    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          <div className="d-flex flex-wrap justify-content-between align-items-start mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("asset_report")}</h3>
              <div className="text-secondary text-14">{t("ah_ov_desc")}</div>
              {data?.as_of ? <div className="text-secondary text-12 mt-1" data-testid="ah-as-of">{fill(t("ah_as_of"), { d: fmtDate(data.as_of) })}</div> : null}
            </div>
            <div className="d-flex flex-wrap align-items-start gap-2">
              <div style={{ minWidth: 240 }} data-testid="ah-f-bu">
                <Select
                  className="form-control w-100"
                  options={departmentOptions}
                  value={department_uid}
                  onChange={(v) => this.SetDepartment(v)}
                  placeholder={t("ah_all_bu")}
                />
              </div>
              <div className="d-flex gap-2 align-items-start">
                <DateRangeFields
                  type={currentLang()}
                  className="form-control bg-white"
                  fieldStyle={{ width: 140 }}
                  startPlaceholder={t("start_date")}
                  endPlaceholder={t("end_date")}
                  startValue={start}
                  endValue={end}
                  onChange={this.SetRange}
                />
              </div>
              <button type="button" className="btn ah-btn-excel px-4 py-2 fw-bold rounded-3" onClick={this.Export} disabled={exporting || !data || !!rangeError} data-testid="ah-export-btn">
                ⬇ Excel
              </button>
            </div>
          </div>
          {rangeError && rangeError !== "required" ? (
            <div className="alert alert-warning py-2 text-14" data-testid="ah-range-error">{t(`ah_range_${rangeError}`)}</div>
          ) : null}
          <div className="text-12 text-secondary mb-3">{t("ah_range_hint")}</div>
          {this.renderBody()}
        </div>
      </div>
    );
  }
}
